import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { MessageCircle, FileText, DollarSign, Shield, Star, CheckCircle, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLeaseStore } from '../stores/leaseStore';
import { useUserStore } from '../stores/userStore';
import PhotoGallery from './PhotoGallery';
import LocationMap from './LocationMap';
import StartConversation from './StartConversation';
import SubleaseRequest from './SubleaseRequest';
import { formatPrice } from '../utils/formatters';

interface ListingDetailsProps {
  listing: any;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  isOwner: boolean;
}

const FullListingDetails: React.FC<ListingDetailsProps> = ({
  listing,
  isFavorite,
  onToggleFavorite,
  isOwner
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { users } = useUserStore();
  const { hasUserApplied } = useLeaseStore();
  const [showConversation, setShowConversation] = useState(false);
  const [showSubleaseRequest, setShowSubleaseRequest] = useState(false);

  const hasApplied = user ? hasUserApplied(user.id, listing.id) : false;
  const owner = users[listing.ownerId];
  const ownerName = owner?.fullName || 'Property Owner';

  const getCreditScoreRange = (score: string) => {
    switch (score) {
      case '850-800': return 'Excellent (800-850)';
      case '799-740': return 'Very Good (740-799)';
      case '739-670': return 'Good (670-739)';
      case '669-580': return 'Fair (580-669)';
      case '579-300': return 'Poor (300-579)';
      default: return 'No minimum requirement';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{listing.title}</h1>
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-600">
              <span>{listing.displayLocation || listing.location}</span>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <PhotoGallery images={listing.images} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Property Details */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">About this property</h2>
              <p className="text-gray-600 leading-relaxed mb-6">{listing.description}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t pt-6">
                <div>
                  <p className="text-sm text-gray-500">Bedrooms</p>
                  <p className="font-medium">{listing.beds === 0 ? 'Studio' : `${listing.beds} bed${listing.beds > 1 ? 's' : ''}`}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bathrooms</p>
                  <p className="font-medium">{listing.baths} bath{listing.baths > 1 ? 's' : ''}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Square Feet</p>
                  <p className="font-medium">{listing.sqft} sqft</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Available</p>
                  <p className="font-medium">{new Date(listing.available).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {listing.amenities.map((amenity: string) => (
                    <div key={amenity} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-gray-700">
                        {amenity.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Applicant Requirements */}
            {listing.applicantCriteria && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-6">Applicant Requirements</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.applicantCriteria.minimumIncome && (
                    <div>
                      <div className="flex items-center mb-2">
                        <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="font-medium text-gray-900">Income Requirement</h3>
                      </div>
                      <p className="text-gray-600">
                        Minimum {formatPrice(parseInt(listing.applicantCriteria.minimumIncome))}/month
                      </p>
                      <p className="text-sm text-gray-500">
                        {(parseInt(listing.applicantCriteria.minimumIncome) / listing.price).toFixed(1)}x monthly rent
                      </p>
                    </div>
                  )}

                  {listing.applicantCriteria.minimumCreditScore && (
                    <div>
                      <div className="flex items-center mb-2">
                        <Star className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="font-medium text-gray-900">Credit Score</h3>
                      </div>
                      <p className="text-gray-600">
                        {getCreditScoreRange(listing.applicantCriteria.minimumCreditScore)}
                      </p>
                    </div>
                  )}

                  {listing.applicantCriteria.employmentVerification && (
                    <div>
                      <div className="flex items-center mb-2">
                        <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="font-medium text-gray-900">Employment</h3>
                      </div>
                      <p className="text-gray-600">Employment verification required</p>
                    </div>
                  )}

                  {listing.applicantCriteria.requireBackgroundCheck && (
                    <div>
                      <div className="flex items-center mb-2">
                        <Shield className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="font-medium text-gray-900">Background Check</h3>
                      </div>
                      <p className="text-gray-600">Background check required</p>
                    </div>
                  )}
                </div>

                {listing.applicantCriteria.additionalRequirements && (
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-medium text-gray-900 mb-2">Additional Requirements</h3>
                    <p className="text-gray-600">{listing.applicantCriteria.additionalRequirements}</p>
                  </div>
                )}
              </div>
            )}

            {/* Location */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Location</h2>
              <p className="text-gray-600">
                Exact location provided after booking. The property is located within the highlighted area.
              </p>
              <LocationMap coordinates={listing.coordinates} />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                <div className="text-2xl font-bold text-indigo-600">
                  {formatPrice(listing.price)}<span className="text-gray-500 text-lg font-normal">/month</span>
                </div>

                {isOwner ? (
                  <Link
                    to={`/dashboard?listing=${listing.id}`}
                    className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl text-center font-semibold hover:bg-gray-200 transition-colors block"
                  >
                    Manage Listing
                  </Link>
                ) : hasApplied ? (
                  <div className="bg-gray-50 px-6 py-3 rounded-xl">
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className="h-5 w-5 text-indigo-600" />
                      <span className="font-medium text-gray-900">Application Submitted</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-center">
                      Check your applications dashboard for status updates
                    </p>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setShowSubleaseRequest(true)}
                      className="w-full bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
                    >
                      Request to Sublease
                    </button>
                    <button
                      onClick={() => setShowConversation(true)}
                      className="w-full bg-white text-indigo-600 px-6 py-3 rounded-xl font-semibold border-2 border-indigo-600 hover:bg-indigo-50 transition-colors flex items-center justify-center"
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Contact Owner
                    </button>
                  </>
                )}

                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                  <p className="font-medium mb-2">🔒 Secure Sublease Process</p>
                  <p>All communication and payments are handled through our secure platform for your protection.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConversation && !isOwner && (
        <StartConversation
          listingId={listing.id}
          listingTitle={listing.title}
          receiverId={listing.ownerId}
          recipientName={ownerName}
          onMessageSent={() => setShowConversation(false)}
        />
      )}

      {showSubleaseRequest && !isOwner && (
        <SubleaseRequest
          listing={listing}
          onClose={() => setShowSubleaseRequest(false)}
        />
      )}
    </div>
  );
};

export default FullListingDetails;